import { useState, useEffect, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ContactFormSection } from '@components/ui/ContactFormSection'
import { ImagePlaceholder } from '@components/ui/ImagePlaceholder'
import { Footer } from '@components/Layout'
import { Seo } from '@components/Seo'
import { useLang, withLang } from '@shared/i18n/useLang'

// ── Lightbox ──────────────────────────────────────────────────────────────────

interface LightboxItem { src: string; caption: string }

function Lightbox({ images, index, onClose, onNav }: {
  images: LightboxItem[]
  index: number
  onClose: () => void
  onNav: (i: number) => void
}) {
  const hasPrev = index > 0
  const hasNext = index < images.length - 1
  const item = images[index]

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
      if (e.key === 'ArrowLeft'  && hasPrev) onNav(index - 1)
      if (e.key === 'ArrowRight' && hasNext) onNav(index + 1)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [index, hasPrev, hasNext, onClose, onNav])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,.88)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      }}
    >
      {/* image + nav */}
      <div
        onClick={e => e.stopPropagation()}
        style={{ position: 'relative', maxWidth: '90vw', maxHeight: '80vh', display: 'flex', alignItems: 'center' }}
      >
        {hasPrev && (
          <button onClick={() => onNav(index - 1)} style={arrowBtn('left')}>&#8592;</button>
        )}

        <img
          src={item.src}
          alt={item.caption}
          style={{ maxWidth: '80vw', maxHeight: '78vh', objectFit: 'contain', display: 'block', borderRadius: 4 }}
        />

        {hasNext && (
          <button onClick={() => onNav(index + 1)} style={arrowBtn('right')}>&#8594;</button>
        )}
      </div>

      {/* caption + counter */}
      <div
        onClick={e => e.stopPropagation()}
        style={{ marginTop: 16, textAlign: 'center', color: '#CBD5E0', fontSize: 13, letterSpacing: '.04em', maxWidth: '60vw' }}
      >
        {item.caption && <div>{item.caption}</div>}
        {images.length > 1 && (
          <div style={{ marginTop: 8, display: 'flex', gap: 6, justifyContent: 'center' }}>
            {images.map((_, i) => (
              <span
                key={i}
                onClick={() => onNav(i)}
                style={{
                  width: 6, height: 6, borderRadius: '50%', cursor: 'pointer',
                  background: i === index ? '#189BD5' : 'rgba(255,255,255,.3)',
                  transition: 'background .2s',
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* close */}
      <button
        onClick={onClose}
        style={{ position: 'fixed', top: 20, right: 24, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer', lineHeight: 1 }}
      >
        ×
      </button>
    </div>
  )
}

function arrowBtn(side: 'left' | 'right'): React.CSSProperties {
  return {
    position: 'absolute', [side]: -56,
    background: 'rgba(255,255,255,.12)', border: 'none', color: '#fff',
    fontSize: 22, width: 44, height: 44, borderRadius: '50%', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background .15s',
  }
}

export interface DirectionPageProps {
  slug: string
  title: string
  cover?: string
  subtitle?: string
  description: string[]
  photos: { caption: string; photo?: string }[]
  services: string[]
  stages: { title: string; desc: string }[]
  advantages: { title: string; desc: string }[]
  projects: { slug: string; title: string; year: string; photo?: string }[]
  certificates: { label: string; photo?: string }[]
  showServices: boolean
  showStages: boolean
  showAdvantages: boolean
  showCertificates: boolean
  showPhotos: boolean
  dev: boolean
}

export function DirectionPage({
  slug,
  title,
  cover,
  subtitle,
  description,
  photos,
  services,
  stages,
  advantages,
  certificates,
  showServices,
  showStages,
  showAdvantages,
  showCertificates,
  showPhotos,
}: DirectionPageProps) {
  const { t } = useTranslation()
  const lang = useLang()
  const finalSubtitle = subtitle ?? t('direction.defaultSubtitle')
  const mid = Math.ceil(services.length / 2)

  const [lb, setLb] = useState<{ images: LightboxItem[]; index: number } | null>(null)
  const closeLb = useCallback(() => setLb(null), [])
  const navLb = useCallback((i: number) => setLb(prev => prev ? { ...prev, index: i } : null), [])

  const photoItems: LightboxItem[] = photos
    .filter(p => p.photo)
    .map(p => ({ src: p.photo!, caption: p.caption }))

  const certItems: LightboxItem[] = certificates
    .filter(c => c.photo)
    .map(c => ({ src: c.photo!, caption: c.label }))

  return (
    <>
      <Seo
        title={title}
        description={finalSubtitle}
        image={cover}
        breadcrumbs={[
          { name: t('common.home'), path: withLang(lang, '/') },
          { name: title, path: withLang(lang, `/napravleniya/${slug}`) },
        ]}
      />

      {/* Page head */}
      <section className="section-hero-bg" style={{ padding: '64px 0' }}>
        <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase' }}>
            <Link to={withLang(lang, '/')} style={{ color: '#5C6670', transition: 'color .15s' }}>{t('common.home')}</Link>
            <span style={{ color: '#3A434D' }}>/</span>
            <span style={{ color: '#93A3AE' }}>{title}</span>
          </div>
          <h1 className="h1-page" style={{ margin: 0, fontSize: 44, fontWeight: 750, letterSpacing: '-.015em', color: '#FFFFFF' }}>{title}</h1>
          <p style={{ margin: 0, fontSize: 16, lineHeight: 1.65, color: '#93A3AE', maxWidth: 640 }}>{finalSubtitle}</p>
        </div>
      </section>

      {/* О направлении */}
      <section className="section-pad">
        <div className="container-page" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          <h2 className="h2-section" style={{ margin: 0, fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('direction.aboutTitle')}</h2>
          <div className="grid-content">
            {cover
              ? <img src={cover} alt={title} style={{ width: '100%', height: 320, objectFit: 'cover', display: 'block', borderRadius: 3, flexShrink: 0 }} />
              : <ImagePlaceholder height={320} />}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              {description.map((p, i) => (
                <p key={i} style={{ margin: 0, fontSize: 15.5, lineHeight: 1.7, color: '#3A434D', hyphens: 'auto', overflowWrap: 'break-word' }}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Преимущества */}
      {showAdvantages && advantages.length > 0 && (
        <section className="section-pad">
          <div className="container-page">
            <h2 className="h2-section" style={{ margin: '0 0 36px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('direction.advantagesTitle')}</h2>
            <div className="grid-4">
              {advantages.map((a, i) => (
                <div key={a.title} className="card" style={{ padding: '26px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>
                  <span style={{ fontSize: 13, fontWeight: 600, color: '#189BD5', letterSpacing: '.06em' }}>{String(i + 1).padStart(2, '0')}</span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 650 as number, color: '#171C22' }}>{a.title}</span>
                    <span style={{ fontSize: 13.5, lineHeight: 1.55, color: '#5C6670' }}>{a.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Перечень услуг */}
      {showServices && services.length > 0 && (
        <section className="section-pad">
          <div className="container-page">
            <div className="card card-pad" style={{ padding: '44px 48px' }}>
              <h2 className="h2-section" style={{ margin: '0 0 32px', fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('direction.servicesTitle')}</h2>
              <div className="grid-pairs">
                {[...services.slice(0, mid), ...services.slice(mid)].map((s, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 12, padding: '10px 0', borderBottom: '1px solid #EEF1F3' }}>
                    <span style={{ width: 6, height: 6, background: '#189BD5', flexShrink: 0, transform: 'translateY(-2px)' }} />
                    <span style={{ fontSize: 14.5, color: '#3A434D' }}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Этапы */}
      {showStages && stages.length > 0 && (
        <section className="section-pad">
          <div className="container-page">
            <div className="card card-pad grid-stages" style={{ padding: '44px 48px' }}>
              <div className="sticky-head" style={{ position: 'sticky', top: 104 }}>
                <h2 style={{ margin: '0 0 16px', fontSize: 30, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('direction.stagesTitle')}</h2>
                <p style={{ margin: 0, fontSize: 14, lineHeight: 1.65, color: '#5C6670' }}>{t('direction.stagesDesc')}</p>
              </div>
              <div>
                {stages.map((s, i) => (
                  <div key={i} style={{ display: 'flex', gap: 24, alignItems: 'flex-start', padding: '18px 0', borderBottom: '1px solid #EEF1F3' }}>
                    <span style={{ width: 36, height: 36, flexShrink: 0, background: '#171C22', color: '#29ABE2', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 3, letterSpacing: '.04em' }}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span style={{ display: 'flex', flexDirection: 'column', gap: 5, paddingTop: 2 }}>
                      <span style={{ fontSize: 16, fontWeight: 650 as number, color: '#171C22' }}>{s.title}</span>
                      <span style={{ fontSize: 13.5, lineHeight: 1.6, color: '#5C6670' }}>{s.desc}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Сертификаты */}
      {showCertificates && certificates.length > 0 && (
        <section className="section-pad">
          <div className="container-page">
            <h2 className="h2-section" style={{ margin: '0 0 36px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('direction.certificatesTitle')}</h2>
            <div className="grid-4">
              {certificates.map((c, i) => {
                const lbIdx = certItems.findIndex(x => x.src === c.photo)
                return (
                  <div key={i} className="card" style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14, alignItems: 'center' }}>
                    {c.photo
                      ? (
                        <div style={{ width: '100%', aspectRatio: '210 / 297', background: '#F4F6F7', borderRadius: 3, overflow: 'hidden' }}>
                          <img
                            src={c.photo} alt={c.label}
                            loading="lazy"
                            onClick={() => setLb({ images: certItems, index: lbIdx })}
                            style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', cursor: 'zoom-in', imageRendering: 'auto' }}
                          />
                        </div>
                      )
                      : <div style={{ width: '100%', aspectRatio: '210 / 297', overflow: 'hidden', borderRadius: 3 }}><ImagePlaceholder height="100%" /></div>}
                    {c.label && <span style={{ fontSize: 13, fontWeight: 600, color: '#171C22', letterSpacing: '.04em' }}>{c.label}</span>}
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* Фотографии */}
      {showPhotos && photos.length > 0 && (
        <section className="section-pad">
          <div className="container-page">
            <h2 className="h2-section" style={{ margin: '0 0 36px', fontSize: 34, fontWeight: 700, letterSpacing: '-.015em', color: '#171C22' }}>{t('direction.photosTitle')}</h2>
            <div className="grid-3">
              {photos.map((p, i) => {
                const lbIdx = photoItems.findIndex(x => x.src === p.photo)
                return (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {p.photo
                      ? (
                        <img
                          src={p.photo} alt={p.caption}
                          loading="lazy"
                          onClick={() => setLb({ images: photoItems, index: lbIdx })}
                          style={{ width: '100%', height: 250, objectFit: 'cover', display: 'block', borderRadius: 3, cursor: 'zoom-in' }}
                        />
                      )
                      : <ImagePlaceholder height={250} />}
                    {p.caption && <span style={{ fontSize: 12.5, color: '#5C6670', letterSpacing: '.04em' }}>{p.caption}</span>}
                  </div>
                )
              })}
            </div>
          </div>
        </section>
      )}

      <div style={{ height: 80 }} />

      <ContactFormSection
        title={t('direction.ctaTitle', { direction: title.toLowerCase() })}
        subtitle={t('direction.ctaSubtitle')}
        direction={title}
      />

      <Footer />

      {lb && <Lightbox images={lb.images} index={lb.index} onClose={closeLb} onNav={navLb} />}
    </>
  )
}
